import React, { useEffect, useState } from 'react'
import {
	Alert,
	AutoComplete,
	Badge,
	Button,
	Card,
	Col,
	Collapse,
	Empty,
	Input,
	Modal,
	Row,
	Select,
	Space,
	Tag,
	Tooltip,
	Typography,
} from 'antd'
import {
	ArrowRightOutlined,
	DeleteOutlined,
	EyeOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	SearchOutlined,
	SwapOutlined,
} from '@ant-design/icons'
import { staffService } from '../../common/service/staff.service'
import { roleService } from '../../common/service'
import { useThemeColors } from '../../common/ThemeContext'

const { Text } = Typography

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReplaceRule {
	id: string
	field: string
	fromValue: string | undefined
	toValue: string | undefined
}

interface OptionItem {
	label: string
	value: string
}

interface FieldDef {
	key: string
	label: string
	type: 'select' | 'text'
	options?: (ctx: OptionCtx) => OptionItem[]
	/** Extract the comparable value from a staff record for client-side filtering */
	getStaffValue: (staff: any) => string | undefined
	/** If true, "From" input is hidden — rule only sets new value, no filter applied */
	toOnly?: boolean
	/** Placeholder text for text input fields */
	placeholder?: string
}

interface OptionCtx {
	shiftOptions: OptionItem[]
	departmentOptions: OptionItem[]
	roleOptions: OptionItem[]
	managerOptions: OptionItem[]
}

interface Props {
	open: boolean
	onClose: () => void
	onSuccess?: () => void
	departmentOptions: OptionItem[]
	shiftOptions: OptionItem[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: OptionItem[] = [
	{ value: 'Active', label: 'Active' },
	{ value: 'InActive', label: 'Inactive' },
]

const RANKING_OPTIONS: OptionItem[] = [
	{ value: 'executor', label: 'Executor' },
	{ value: 'manager_level_1', label: 'Manager Lv.1' },
	{ value: 'manager_level_2', label: 'Manager Lv.2' },
	{ value: 'manager_level_3', label: 'Manager Lv.3' },
]

const BOOLEAN_OPTIONS: OptionItem[] = [
	{ value: 'true', label: 'Enabled' },
	{ value: 'false', label: 'Disabled' },
]

const SEX_OPTIONS: OptionItem[] = [
	{ value: 'male', label: 'Male' },
	{ value: 'female', label: 'Female' },
]

const FIELD_DEFS: FieldDef[] = [
	// ── Select fields ────────────────────────────────────────────────────────────
	{
		key: 'shiftId',
		label: 'Shift / Team',
		type: 'select',
		options: (ctx) => ctx.shiftOptions,
		getStaffValue: (s) => s.shift?.id,
	},
	{
		key: 'departmentId',
		label: 'Department',
		type: 'select',
		options: (ctx) => ctx.departmentOptions,
		getStaffValue: (s) => s.departmentId,
	},
	{
		key: 'status',
		label: 'Status',
		type: 'select',
		options: () => STATUS_OPTIONS,
		getStaffValue: (s) => s.status,
	},
	{
		key: 'ranking',
		label: 'Ranking',
		type: 'select',
		options: () => RANKING_OPTIONS,
		getStaffValue: (s) => s.ranking,
	},
	{
		key: 'roleId',
		label: 'Role',
		type: 'select',
		options: (ctx) => ctx.roleOptions,
		getStaffValue: (s) => s.role?.id,
	},
	{
		key: 'sex',
		label: 'Sex',
		type: 'select',
		options: () => SEX_OPTIONS,
		getStaffValue: (s) => s.sex,
	},
	{
		key: 'extraShift',
		label: 'Night Shift',
		type: 'select',
		options: (ctx) => ctx.shiftOptions,
		getStaffValue: (s) => s.extraShift,
	},
	{
		key: 'isExtraShift',
		label: 'Night Shift Enabled',
		type: 'select',
		options: () => BOOLEAN_OPTIONS,
		getStaffValue: (s) => (s.isExtraShift != null ? String(s.isExtraShift) : undefined),
		toOnly: true,
	},
	{
		key: 'firstLineManager',
		label: 'Direct Manager',
		type: 'select',
		options: (ctx) => ctx.managerOptions,
		getStaffValue: (s) => s.firstLineManager?.id,
	},
	{
		key: 'secondLineManager',
		label: '2nd Line Manager',
		type: 'select',
		options: (ctx) => ctx.managerOptions,
		getStaffValue: (s) => s.secondLineManager?.id,
	},
	{
		key: 'thirdLineManager',
		label: '3rd Line Manager',
		type: 'select',
		options: (ctx) => ctx.managerOptions,
		getStaffValue: (s) => s.thirdLineManager?.id,
	},
	// ── Text fields ──────────────────────────────────────────────────────────────
	{
		key: 'firstName',
		label: 'First Name',
		type: 'text',
		getStaffValue: (s) => s.firstName,
		placeholder: 'e.g. John',
	},
	{
		key: 'lastName',
		label: 'Last Name',
		type: 'text',
		getStaffValue: (s) => s.lastName,
		placeholder: 'e.g. Doe',
	},
	{
		key: 'nameInEnglish',
		label: 'Name in English',
		type: 'text',
		getStaffValue: (s) => s.nameInEnglish,
		placeholder: 'e.g. John Doe',
	},
	{
		key: 'email',
		label: 'Email',
		type: 'text',
		getStaffValue: (s) => s.email,
		placeholder: 'e.g. staff@company.com',
	},
	{
		key: 'phoneNumber',
		label: 'Phone Number',
		type: 'text',
		getStaffValue: (s) => s.phoneNumber,
		placeholder: 'e.g. 012345678',
	},
	{
		key: 'bankAccountNumber',
		label: 'Bank Account No.',
		type: 'text',
		getStaffValue: (s) => s.bankAccountNumber,
		placeholder: 'e.g. 000-00-000000-0',
	},
]

const ALL_FIELD_KEYS = FIELD_DEFS.map((f) => f.key)

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _counter = 0
const nextId = () => `rule-${++_counter}-${Date.now()}`
const makeRule = (): ReplaceRule => ({
	id: nextId(),
	field: '',
	fromValue: undefined,
	toValue: undefined,
})

const findDef = (key: string) => FIELD_DEFS.find((d) => d.key === key)

const optionLabel = (options: OptionItem[], value?: string): string => {
	if (!value) return '—'
	return options.find((o) => o.value === value)?.label ?? value
}

// ─── Component ────────────────────────────────────────────────────────────────

const StaffMassEdit: React.FC<Props> = ({
	open,
	onClose,
	onSuccess,
	departmentOptions,
	shiftOptions,
}) => {
	const { primary } = useThemeColors()
	// ── State ──────────────────────────────────────────────────────────────────
	const [scopeDeptId, setScopeDeptId] = useState<string | undefined>(undefined)
	const [rules, setRules] = useState<ReplaceRule[]>([makeRule()])
	const [roleOptions, setRoleOptions] = useState<OptionItem[]>([])
	const [previewLoading, setPreviewLoading] = useState(false)
	const [previewStaff, setPreviewStaff] = useState<any[]>([])
	const [hasSearched, setHasSearched] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [managerStaff, setManagerStaff] = useState<any[]>([])

	const managerOptions: OptionItem[] = managerStaff.map((s) => ({
		label: s.nameInEnglish ?? `${s.lastName ?? ''} ${s.firstName ?? ''}`.trim(),
		value: s.id,
	}))

	const optionCtx: OptionCtx = { shiftOptions, departmentOptions, roleOptions, managerOptions }

	// ── Load roles & manager options when modal opens ────────────────────────────
	useEffect(() => {
		if (!open) return
		roleService
			.fetchListing()
			.then((res: any) => {
				if (res?.statusCode === 200) {
					setRoleOptions(
						(res.data ?? []).map((r: any) => ({ label: r.name, value: r.id })),
					)
				}
			})
			.catch(() => {})
		staffService
			.fetchListing({ limit: 1000 })
			.then((res: any) => {
				setManagerStaff(Array.isArray(res?.data) ? res.data : [])
			})
			.catch(() => {})
	}, [open])

	// ── Reset on close ─────────────────────────────────────────────────────────
	const resetAll = () => {
		setScopeDeptId(undefined)
		setRules([makeRule()])
		setPreviewStaff([])
		setHasSearched(false)
	}

	// ── Rule mutations ─────────────────────────────────────────────────────────
	const addRule = () => {
		const usedFields = rules.map((r) => r.field).filter(Boolean)
		const nextField = ALL_FIELD_KEYS.find((k) => !usedFields.includes(k)) ?? ''
		setRules((prev) => [...prev, { ...makeRule(), field: nextField }])
		setHasSearched(false)
	}

	const removeRule = (id: string) => {
		setRules((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev))
		setHasSearched(false)
	}

	const changeField = (id: string, field: string) => {
		setRules((prev) =>
			prev.map((r) =>
				r.id === id ? { ...r, field, fromValue: undefined, toValue: undefined } : r,
			),
		)
		setHasSearched(false)
	}

	const changeFrom = (id: string, fromValue: string | undefined) => {
		setRules((prev) => prev.map((r) => (r.id === id ? { ...r, fromValue } : r)))
		setHasSearched(false)
	}

	const changeTo = (id: string, toValue: string | undefined) => {
		setRules((prev) => prev.map((r) => (r.id === id ? { ...r, toValue } : r)))
	}

	// ── Preview ────────────────────────────────────────────────────────────────
	const handlePreview = async () => {
		const validRules = rules.filter((r) => r.field)
		if (validRules.length === 0) {
			Modal.warning({ title: 'No rules defined', content: 'Add at least one field rule.' })
			return
		}

		setPreviewLoading(true)
		setHasSearched(true)

		try {
			const res: any = await staffService.fetchListing({
				departmentId: scopeDeptId || undefined,
				limit: 1000,
			})
			const allStaff: any[] = Array.isArray(res?.data) ? res.data : []

			// Rules that act as filters: have a fromValue and are not toOnly
			const filterRules = validRules.filter(
				(r) => !findDef(r.field)?.toOnly && r.fromValue !== undefined && r.fromValue !== '',
			)

			const matched =
				filterRules.length === 0
					? allStaff
					: allStaff.filter((staff) =>
							filterRules.every((rule) => {
								const def = findDef(rule.field)
								const staffVal = def?.getStaffValue?.(staff)
								if (def?.type === 'text') {
									return (
										(staffVal ?? '').toLowerCase().trim() ===
										(rule.fromValue ?? '').toLowerCase().trim()
									)
								}
								return staffVal === rule.fromValue
							}),
						)

			setPreviewStaff(matched)
		} catch (e: any) {
			Modal.error({ title: 'Failed to load staff', content: e?.message ?? 'Unknown error' })
		} finally {
			setPreviewLoading(false)
		}
	}

	// ── Apply ──────────────────────────────────────────────────────────────────
	const handleApply = async () => {
		if (!hasSearched || previewStaff.length === 0) {
			Modal.warning({
				title: 'Run a preview first',
				content: 'Click "Find Matching Staff" to preview which records will be updated.',
			})
			return
		}

		const updateRules = rules.filter(
			(r) => r.field && r.toValue !== undefined && r.toValue !== '',
		)
		if (updateRules.length === 0) {
			Modal.warning({
				title: 'No new values set',
				content: 'Enter a "New Value" for at least one rule.',
			})
			return
		}

		const fields: Record<string, any> = {}
		for (const rule of updateRules) {
			if (rule.field === 'isExtraShift') {
				fields[rule.field] = rule.toValue === 'true'
			} else if (
				rule.field === 'firstLineManager' ||
				rule.field === 'secondLineManager' ||
				rule.field === 'thirdLineManager'
			) {
				const mgr = managerStaff.find((s) => s.id === rule.toValue)
				if (mgr) {
					fields[rule.field] = {
						id: mgr.id,
						name:
							mgr.nameInEnglish ??
							`${mgr.lastName ?? ''} ${mgr.firstName ?? ''}`.trim(),
						photoUrl: mgr.photoUrl ?? '',
					}
				}
			} else {
				fields[rule.field] = rule.toValue
			}
		}

		const staffIds = previewStaff.map((s) => s.id)

		Modal.confirm({
			title: `Apply changes to ${staffIds.length} staff?`,
			content: (
				<div>
					<p style={{ color: '#64748b', marginBottom: 8 }}>
						The following fields will be updated on {staffIds.length} staff record
						{staffIds.length !== 1 ? 's' : ''}:
					</p>
					{updateRules.map((r) => {
						const def = findDef(r.field)
						const opts = def?.options ? def.options(optionCtx) : []
						const displayVal =
							def?.type === 'text' ? (r.toValue ?? '—') : optionLabel(opts, r.toValue)
						return (
							<div key={r.id} style={{ fontSize: 13, marginBottom: 4 }}>
								<Tag color='blue' style={{ marginRight: 4 }}>
									{def?.label ?? r.field}
								</Tag>
								→ <strong>{displayVal}</strong>
							</div>
						)
					})}
				</div>
			),
			okText: `Apply to ${staffIds.length} Staff`,
			okType: 'primary',
			cancelText: 'Cancel',
			onOk: async () => {
				setSubmitting(true)
				try {
					await staffService.massEdit({ staffIds, fields })
					Modal.success({
						title: 'Mass edit applied',
						content: `Successfully updated ${staffIds.length} staff record${staffIds.length !== 1 ? 's' : ''}.`,
					})
					onSuccess?.()
					onClose()
					resetAll()
				} catch (e: any) {
					Modal.error({
						title: 'Mass edit failed',
						content:
							e?.response?.data?.message ??
							e?.message ??
							'An unexpected error occurred.',
					})
				} finally {
					setSubmitting(false)
				}
			},
		})
	}

	const handleClose = () => {
		onClose()
		resetAll()
	}

	// ── Derived ────────────────────────────────────────────────────────────────
	const usedFields = rules.map((r) => r.field).filter(Boolean)
	const hasActiveFilters = rules.some((r) => r.field && !findDef(r.field)?.toOnly && r.fromValue)

	const getFieldOptions = (currentField: string) =>
		FIELD_DEFS.map((def) => ({
			label: def.label,
			value: def.key,
			disabled: def.key !== currentField && usedFields.includes(def.key),
		}))

	// ── Value Select ───────────────────────────────────────────────────────────
	const renderValueInput = (
		_ruleId: string,
		field: string,
		value: string | undefined,
		onChange: (v: string | undefined) => void,
		placeholder: string,
		disabled?: boolean,
	) => {
		const def = findDef(field)
		if (!def)
			return <Select style={{ width: '100%' }} placeholder='Select field first' disabled />
		if (def.type === 'text') {
			return (
				<Input
					style={{ width: '100%' }}
					allowClear
					placeholder={disabled ? '(any value)' : (def.placeholder ?? placeholder)}
					disabled={disabled}
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value || undefined)}
				/>
			)
		}
		const opts = def.options ? def.options(optionCtx) : []
		return (
			<AutoComplete
				style={{ width: '100%' }}
				allowClear
				placeholder={disabled ? '(any)' : placeholder}
				disabled={disabled}
				options={opts.map((o: any) => ({ value: String(o.value ?? ''), label: o.label }))}
				value={value ?? ''}
				onChange={(v) => onChange(v || undefined)}
				filterOption={(input, opt) =>
					((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase()) ||
					((opt?.value as string) ?? '').toLowerCase().includes(input.toLowerCase())
				}
			/>
		)
	}

	// ── Preview list ───────────────────────────────────────────────────────────
	const renderPreviewList = () => {
		if (!hasSearched) return null

		if (previewStaff.length === 0) {
			return (
				<Empty
					description={
						<Text type='secondary'>No staff matched the current criteria.</Text>
					}
					style={{ padding: '16px 0' }}
				/>
			)
		}

		const filterRules = rules.filter((r) => r.field && !findDef(r.field)?.toOnly && r.fromValue)

		return (
			<Collapse
				size='small'
				ghost
				style={{ marginTop: 8 }}
				items={[
					{
						key: 'list',
						label: (
							<Space>
								<EyeOutlined />
								<Text strong>View {previewStaff.length} matched staff</Text>
							</Space>
						),
						children: (
							<div
								style={{
									maxHeight: 280,
									overflowY: 'auto',
									border: '1px solid #f0f0f0',
									borderRadius: 6,
								}}
							>
								{previewStaff.map((staff, i) => (
									<div
										key={staff.id}
										style={{
											display: 'flex',
											alignItems: 'center',
											padding: '6px 10px',
											borderBottom:
												i < previewStaff.length - 1
													? '1px solid #f0f0f0'
													: 'none',
											fontSize: 13,
											gap: 8,
											background: i % 2 === 0 ? '#fff' : '#fafafa',
										}}
									>
										<Tag
											style={{ minWidth: 60, textAlign: 'center', margin: 0 }}
										>
											{staff.id}
										</Tag>
										<Text style={{ flex: 1, minWidth: 0 }} ellipsis>
											{staff.nameInEnglish ??
												`${staff.lastName ?? ''} ${staff.firstName ?? ''}`.trim()}
										</Text>
										{filterRules.map((rule) => {
											const def = findDef(rule.field)!
											const opts = def.options ? def.options(optionCtx) : []
											const current = def.getStaffValue(staff)
											const fromLabel =
												def.type === 'text'
													? (current ?? '—')
													: optionLabel(opts, current)
											const toLabel =
												def.type === 'text'
													? (rule.toValue ?? '—')
													: optionLabel(opts, rule.toValue)
											return (
												<Space
													key={rule.id}
													size={4}
													style={{ flexShrink: 0 }}
												>
													<Tag
														color='orange'
														style={{ margin: 0, fontSize: 11 }}
													>
														{fromLabel}
													</Tag>
													<ArrowRightOutlined
														style={{ fontSize: 10, color: '#94a3b8' }}
													/>
													<Tag
														color='green'
														style={{ margin: 0, fontSize: 11 }}
													>
														{toLabel}
													</Tag>
												</Space>
											)
										})}
									</div>
								))}
							</div>
						),
					},
				]}
			/>
		)
	}

	// ── Render ─────────────────────────────────────────────────────────────────
	return (
		<Modal
			title={
				<Space>
					<SwapOutlined style={{ color: primary }} />
					<span>Mass Edit Staff Fields</span>
				</Space>
			}
			open={open}
			onCancel={handleClose}
			width={860}
			destroyOnHidden
			footer={[
				<Button key='cancel' onClick={handleClose}>
					Cancel
				</Button>,
				<Button
					key='apply'
					type='primary'
					loading={submitting}
					disabled={!hasSearched || previewStaff.length === 0}
					onClick={handleApply}
					icon={<SwapOutlined />}
				>
					Apply to {previewStaff.length} Staff
				</Button>,
			]}
		>
			<Space direction='vertical' style={{ width: '100%' }} size={16}>
				{/* ── 1. Scope ───────────────────────────────────────────────── */}
				<Card
					size='small'
					title={
						<Space>
							<Text strong>1. Scope</Text>
							<Tooltip title='Optionally narrow the search to a specific department. Leave empty to search all staff.'>
								<InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
							</Tooltip>
						</Space>
					}
					styles={{ body: { padding: '12px 16px' } }}
				>
					<Row gutter={12} align='middle'>
						<Col flex='110px'>
							<Text type='secondary' style={{ fontSize: 13 }}>
								Department
							</Text>
						</Col>
						<Col flex='auto'>
							<Select
								style={{ width: '100%', maxWidth: 320 }}
								allowClear
								showSearch
								placeholder='All Departments'
								options={departmentOptions}
								value={scopeDeptId}
								onChange={(v) => {
									setScopeDeptId(v)
									setHasSearched(false)
								}}
								filterOption={(input, opt) =>
									((opt?.label as string) ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							/>
						</Col>
					</Row>
				</Card>

				{/* ── 2. Field Rules ─────────────────────────────────────────── */}
				<Card
					size='small'
					title={
						<Space>
							<Text strong>2. Field Replacement Rules</Text>
							<Tooltip title='Set the field, its Current Value to find (leave empty to match all), and the New Value to apply.'>
								<InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
							</Tooltip>
						</Space>
					}
					extra={
						rules.length < FIELD_DEFS.length ? (
							<Button
								type='link'
								icon={<PlusOutlined />}
								size='small'
								onClick={addRule}
							>
								Add Rule
							</Button>
						) : null
					}
					styles={{ body: { padding: 0 } }}
				>
					{/* Column Headers */}
					<Row
						gutter={8}
						align='middle'
						style={{
							padding: '8px 16px',
							background: '#fafafa',
							borderBottom: '1px solid #f0f0f0',
						}}
					>
						<Col flex='175px'>
							<Text type='secondary' style={{ fontSize: 12 }}>
								Field
							</Text>
						</Col>
						<Col flex='auto'>
							<Space size={4}>
								<Text type='secondary' style={{ fontSize: 12 }}>
									Current Value
								</Text>
								<Text type='secondary' style={{ fontSize: 11 }}>
									(empty = any)
								</Text>
							</Space>
						</Col>
						<Col flex='28px' />
						<Col flex='auto'>
							<Text type='secondary' style={{ fontSize: 12 }}>
								New Value
							</Text>
						</Col>
						<Col flex='32px' />
					</Row>

					{/* Rule rows */}
					{rules.map((rule, idx) => {
						const def = findDef(rule.field)
						const isToOnly = def?.toOnly ?? false

						return (
							<Row
								key={rule.id}
								gutter={8}
								align='middle'
								style={{
									padding: '10px 16px',
									borderBottom:
										idx < rules.length - 1 ? '1px solid #f5f5f5' : 'none',
								}}
							>
								{/* Field selector */}
								<Col flex='175px'>
									<Select
										style={{ width: '100%' }}
										placeholder='Select field…'
										options={getFieldOptions(rule.field)}
										value={rule.field || undefined}
										onChange={(v) => changeField(rule.id, v)}
									/>
								</Col>

								{/* From (current) value */}
								<Col flex='auto'>
									{isToOnly ? (
										<Tooltip title='This field does not support "from" filtering — it will update all matched staff.'>
											<div
												style={{
													height: 32,
													display: 'flex',
													alignItems: 'center',
													paddingLeft: 12,
													color: '#94a3b8',
													fontSize: 12,
													background: '#fafafa',
													borderRadius: 6,
													border: '1px dashed #e2e8f0',
												}}
											>
												— applies to all —
											</div>
										</Tooltip>
									) : (
										renderValueInput(
											rule.id,
											rule.field,
											rule.fromValue,
											(v) => changeFrom(rule.id, v),
											'Any value (no filter)',
										)
									)}
								</Col>

								{/* Arrow */}
								<Col flex='28px' style={{ textAlign: 'center' }}>
									<ArrowRightOutlined
										style={{
											color: rule.toValue ? primary : '#d1d5db',
											fontSize: 13,
										}}
									/>
								</Col>

								{/* To (new) value */}
								<Col flex='auto'>
									{renderValueInput(
										rule.id,
										rule.field,
										rule.toValue,
										(v) => changeTo(rule.id, v),
										'Set new value…',
									)}
								</Col>

								{/* Remove */}
								<Col flex='32px' style={{ textAlign: 'center' }}>
									<Tooltip title='Remove rule'>
										<Button
											type='text'
											size='small'
											danger
											disabled={rules.length === 1}
											icon={<DeleteOutlined />}
											onClick={() => removeRule(rule.id)}
										/>
									</Tooltip>
								</Col>
							</Row>
						)
					})}

					{/* Add rule footer */}
					{rules.length < FIELD_DEFS.length && (
						<div
							style={{
								padding: '8px 16px',
								borderTop: '1px solid #f0f0f0',
							}}
						>
							<Button
								type='dashed'
								icon={<PlusOutlined />}
								onClick={addRule}
								block
								size='small'
							>
								Add Another Rule
							</Button>
						</div>
					)}
				</Card>

				{/* ── 3. Preview ─────────────────────────────────────────────── */}
				<Card
					size='small'
					title={
						<Space>
							<Text strong>3. Preview & Apply</Text>
						</Space>
					}
					extra={
						hasSearched ? (
							<Badge
								count={previewStaff.length}
								showZero
								style={{
									backgroundColor:
										previewStaff.length > 0 ? primary : '#d9d9d9',
								}}
								overflowCount={999}
							/>
						) : null
					}
					styles={{ body: { padding: '12px 16px' } }}
				>
					{!hasActiveFilters && !hasSearched && (
						<Alert
							type='warning'
							showIcon
							message='No "Current Value" filter set'
							description='Without a Current Value, ALL staff in scope will be matched. Add a Current Value to narrow the selection.'
							style={{ marginBottom: 12, fontSize: 12 }}
						/>
					)}

					<Button
						icon={<SearchOutlined />}
						onClick={handlePreview}
						loading={previewLoading}
						block
					>
						{hasSearched ? 'Re-run Search' : 'Find Matching Staff'}
					</Button>

					{hasSearched && (
						<div style={{ marginTop: 10 }}>
							{previewStaff.length > 0 ? (
								<>
									<Text style={{ color: primary, fontWeight: 600 }}>
										{previewStaff.length} staff matched
									</Text>
									{renderPreviewList()}
								</>
							) : (
								<Empty
									description={
										<Text type='secondary'>
											No staff matched the current criteria.
										</Text>
									}
									style={{ padding: '16px 0' }}
								/>
							)}
						</div>
					)}
				</Card>
			</Space>
		</Modal>
	)
}

export default StaffMassEdit
